import { useRef } from 'react';
import { cn } from '@/lib/utils';

const getColumnLetter = (index) => {
  let letter = '';
  let i = index;
  while (i >= 0) {
    letter = String.fromCharCode((i % 26) + 65) + letter;
    i = Math.floor(i / 26) - 1;
  }
  return letter;
};

export function SpreadsheetPreview({
  data = [],
  maxRows = 15,
  highlightHeaderRow = null,
  highlightNameCol = null,
  highlightEmailCol = null,
  highlightQualCols = null,
  onClickRow,
  onClickCol,
  clickMode,
  className
}) {
  const containerRef = useRef(null);

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center p-8 text-sm text-muted-foreground border rounded-lg bg-muted/30">
        No data to preview
      </div>
    );
  }

  const displayData = data.slice(0, maxRows);
  const maxCols = Math.max(...data.slice(0, maxRows).map(row => (row || []).length), 0);
  const hasMore = data.length > maxRows;

  const isRowClickable = clickMode === 'headerRow';
  const isColClickable = ['nameCol', 'emailCol', 'qualStart', 'qualEnd'].includes(clickMode);

  const getCellHighlight = (rowIndex, colIndex) => {
    if (highlightHeaderRow !== null && rowIndex === highlightHeaderRow) {
      return 'bg-blue-100 font-semibold text-blue-900';
    }
    if (highlightNameCol !== null && colIndex === highlightNameCol && rowIndex !== highlightHeaderRow) {
      return 'bg-green-50 text-green-900';
    }
    if (highlightEmailCol !== null && colIndex === highlightEmailCol && rowIndex !== highlightHeaderRow) {
      return 'bg-emerald-50 text-emerald-900';
    }
    if (highlightQualCols && colIndex >= highlightQualCols[0] && colIndex <= highlightQualCols[1] && rowIndex !== highlightHeaderRow) {
      return 'bg-purple-50 text-purple-900';
    }
    return '';
  };

  const getColHeaderHighlight = (colIndex) => {
    if (highlightNameCol !== null && colIndex === highlightNameCol) {
      return 'bg-green-200 text-green-800';
    }
    if (highlightEmailCol !== null && colIndex === highlightEmailCol) {
      return 'bg-emerald-200 text-emerald-800';
    }
    if (highlightQualCols && colIndex >= highlightQualCols[0] && colIndex <= highlightQualCols[1]) {
      return 'bg-purple-200 text-purple-800';
    }
    return 'bg-gray-100 text-gray-500';
  };

  const getRowHeaderHighlight = (rowIndex) => {
    if (highlightHeaderRow !== null && rowIndex === highlightHeaderRow) {
      return 'bg-blue-200 text-blue-800';
    }
    return 'bg-gray-100 text-gray-500';
  };

  return (
    <div ref={containerRef} className={cn('border rounded-lg overflow-hidden bg-white', className)}>
      <div className="overflow-auto max-h-[350px]">
        <table className="border-collapse text-xs w-full">
          <thead className="sticky top-0 z-10">
            <tr>
              <th className="bg-gray-100 border border-gray-300 px-2 py-1 text-xs font-medium text-gray-500 min-w-[40px] sticky left-0 z-20">
              </th>
              {Array.from({ length: maxCols }, (_, i) => (
                <th
                  key={i}
                  onClick={() => isColClickable && onClickCol?.(i)}
                  className={cn(
                    'border border-gray-300 px-2 py-1 text-xs font-medium min-w-[80px]',
                    getColHeaderHighlight(i),
                    isColClickable && 'cursor-pointer hover:bg-amber-100 hover:text-amber-800 transition-colors'
                  )}
                >
                  {getColumnLetter(i)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayData.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className={cn(
                  isRowClickable && 'cursor-pointer hover:!bg-amber-50'
                )}
                onClick={() => isRowClickable && onClickRow?.(rowIndex)}
              >
                <td
                  className={cn(
                    'border border-gray-300 px-2 py-1 text-xs font-medium text-center sticky left-0',
                    getRowHeaderHighlight(rowIndex),
                    isRowClickable && 'cursor-pointer hover:bg-amber-100 hover:text-amber-800 transition-colors'
                  )}
                >
                  {rowIndex + 1}
                </td>
                {Array.from({ length: maxCols }, (_, colIndex) => {
                  const val = row?.[colIndex];
                  const displayVal = val !== undefined && val !== null ? String(val) : '';
                  return (
                    <td
                      key={colIndex}
                      className={cn(
                        'border border-gray-200 px-2 py-1 whitespace-nowrap max-w-[180px] truncate',
                        getCellHighlight(rowIndex, colIndex),
                        isColClickable && 'cursor-pointer'
                      )}
                      onClick={(e) => {
                        if (isColClickable) {
                          e.stopPropagation();
                          onClickCol?.(colIndex);
                        }
                      }}
                      title={displayVal}
                    >
                      {displayVal}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {hasMore && (
        <div className="px-3 py-1.5 bg-gray-50 border-t text-xs text-muted-foreground text-center">
          Showing {maxRows} of {data.length} rows
        </div>
      )}
    </div>
  );
}
